import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import ProfileNav from './ProfileNav';
import BurnCredits from './BurnCredits';
import CreditHistory from './CreditHistory';
import FiatBuyCredits from './FiatBuyCredits';
import CryptoBuyCredits from './CryptoBuyCredits';
import { trackEvent } from '../utils/analytics';
import { peekPendingPhantomTxOperation } from '../utils/phantomDeeplink';

type PurchaseMethod = 'burn' | 'crypto' | 'fiat';

const CreditsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, isLoading, refreshUser } = useAuth();
  const purchaseStatus = searchParams.get('purchase'); // 'success' | 'canceled'

  // Bumped after any successful purchase (burn / crypto / Stripe return).
  // Used as the `key` on <CreditHistory> so it remounts and re-fetches.
  const [historyRefreshTick, setHistoryRefreshTick] = useState(0);
  const onCreditsAdded = useCallback(() => {
    refreshUser();
    setHistoryRefreshTick(t => t + 1);
  }, [refreshUser]);

  // Stripe redirects back with ?purchase=success|canceled — refresh
  // user (success only) to pull the freshly-granted credits, fire the
  // SPA-visible funnel event for either outcome, and clear the param
  // from the URL.
  useEffect(() => {
    if (purchaseStatus === 'success' || purchaseStatus === 'canceled') {
      trackEvent(
        purchaseStatus === 'success'
          ? 'stripe_credits_checkout_completed'
          : 'stripe_credits_checkout_canceled',
        { source: 'credits_page' },
      );
      if (purchaseStatus === 'success') onCreditsAdded();
      const t = setTimeout(() => {
        searchParams.delete('purchase');
        setSearchParams(searchParams, { replace: true });
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [purchaseStatus, onCreditsAdded, searchParams, setSearchParams]);

  // Smart default: $DNET holders → burn; wallet w/o $DNET → crypto; no wallet → fiat.
  // Override: if we just came back from a Phantom-deeplink-tx for a
  // specific method (`?phantom-tx-pending=1` + a stashed operation),
  // mount THAT method's component so its on-mount continuation can
  // consume the result. Otherwise the smart default ('burn' for DNET
  // holders) would mount the wrong component and the result would
  // sit in localStorage unconsumed.
  const smartDefault = useMemo<PurchaseMethod>(() => {
    if (searchParams.has('phantom-tx-pending')) {
      const op = peekPendingPhantomTxOperation();
      if (op === 'buy-credits') return 'crypto';
      if (op === 'burn-credits') return 'burn';
    }
    if (!user?.walletAddress) return 'fiat';
    if ((user?.dnetBalance ?? 0) > 0) return 'burn';
    return 'crypto';
  }, [user?.walletAddress, user?.dnetBalance, searchParams]);
  const [method, setMethod] = useState<PurchaseMethod>(smartDefault);
  // Re-sync when user data first loads (smart default flips after auth resolves)
  useEffect(() => { setMethod(smartDefault); }, [smartDefault]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) navigate('/');
  }, [isLoading, isAuthenticated, navigate]);

  // Funnel: track when the page is viewed and the resolved smart default.
  useEffect(() => { trackEvent('credits_page_viewed', { defaultMethod: smartDefault }); }, [smartDefault]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-crypto-dark">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-crypto-accent border-t-transparent rounded-full"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-crypto-dark">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-white mb-6">Account</h1>
        <ProfileNav />

        {purchaseStatus === 'success' && (
          <div className="mb-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-green-400 text-sm">✓ Payment received. Your credits will appear within a few seconds.</p>
          </div>
        )}
        {purchaseStatus === 'canceled' && (
          <div className="mb-4 p-3 bg-gray-700/40 border border-gray-600 rounded-lg">
            <p className="text-gray-300 text-sm">Checkout canceled — no charge was made.</p>
          </div>
        )}

        {/* Credits balance + burn */}
        <div className="bg-crypto-gray border border-gray-700 rounded-xl p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Credits</h2>
            <div className="text-right">
              <div className="text-2xl font-bold text-crypto-accent">{user.credits.toLocaleString()}</div>
              <p className="text-gray-400 text-xs">available</p>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-4">
            {/* Method selector — segmented control */}
            <div className="flex bg-crypto-dark rounded-lg p-1 mb-5 border border-gray-700">
              {([
                { id: 'burn',   label: '🔥 Burn $DNET' },
                { id: 'crypto', label: '🪙 Crypto' },
                { id: 'fiat',   label: '💳 Card' },
              ] as { id: PurchaseMethod; label: string }[]).map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setMethod(opt.id)}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    method === opt.id
                      ? 'bg-crypto-gray text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {method === 'burn' && (
              <>
                <BurnCredits onCreditsAdded={onCreditsAdded} />
                <p className="text-center text-gray-500 text-xs mt-3">
                  <Link to="/whitepaper" className="text-crypto-accent hover:text-crypto-accent/80 transition-colors">
                    Learn more about $DNET →
                  </Link>
                </p>
              </>
            )}

            {method === 'crypto' && <CryptoBuyCredits onCreditsAdded={onCreditsAdded} />}

            {method === 'fiat' && <FiatBuyCredits />}
          </div>
        </div>

        {/* Credit history */}
        <div className="bg-crypto-gray border border-gray-700 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Credit History</h2>
          <CreditHistory key={historyRefreshTick} />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CreditsPage;
